import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Tabs,
  Card,
  Badge,
  Grid,
  Stack,
  Group,
  Skeleton,
  Alert,
  Paper,
  Button,
  Divider,
  Progress,
  ActionIcon,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { IconArrowLeft, IconAlertCircle, IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import { format } from 'date-fns';
import fiConsoleApi from '../services/fiConsoleApi';
import type {
  GuidelineDetailResponse,
  ComplianceControl,
  ComplianceGap,
  ComplianceTrendDataPoint,
  AssessmentStatus,
} from '../types/fiConsole';

/**
 * GuidelineDetailPage Component
 *
 * Displays detailed compliance information for a specific business guideline including:
 * - Overview with compliance score and metadata
 * - Controls library with status badges
 * - Gaps filtered to this guideline
 * - Compliance trend chart over time
 */
export default function GuidelineDetailPage() {
  const { guideline_id } = useParams<{ guideline_id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<GuidelineDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!guideline_id) {
      setError('No guideline ID provided');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fiConsoleApi.getGuidelineDetail(guideline_id);
        setData(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load guideline details');
        console.error('Error fetching guideline detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [guideline_id]);

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Skeleton height={60} />
          <Skeleton height={400} />
          <Skeleton height={300} />
        </Stack>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Guideline"
          color="red"
        >
          {error || 'Unable to load guideline details'}
        </Alert>
        <Button mt="md" onClick={() => navigate('/fi-console')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const { guideline, semantic_contract, controls, gaps, compliance_history } = data;

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group mb="xl">
        <ActionIcon
          variant="subtle"
          onClick={() => navigate('/fi-console')}
          aria-label="Back to dashboard"
        >
          <IconArrowLeft size={20} />
        </ActionIcon>
        <div style={{ flex: 1 }}>
          <Group justify="apart" align="flex-start">
            <div>
              <Title order={1}>{guideline.guideline_name}</Title>
              <Text c="dimmed" size="sm" mt={4}>
                {guideline.guideline_id}
              </Text>
            </div>
            <Group>
              <ComplianceScoreBadge score={guideline.compliance_score} />
              {guideline.trend && <TrendBadge trend={guideline.trend} />}
            </Group>
          </Group>
        </div>
      </Group>

      {/* Summary Cards */}
      <Grid mb="xl">
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Controls"
            value={guideline.total_controls}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Compliant"
            value={guideline.compliant_controls}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Gaps"
            value={guideline.gaps_count}
            color="orange"
            critical={guideline.critical_gaps_count}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Not Tested"
            value={guideline.not_tested_controls}
            color="gray"
          />
        </Grid.Col>
      </Grid>

      {/* Tabs */}
      <Tabs defaultValue="overview" variant="outline">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="controls">Controls ({controls.length})</Tabs.Tab>
          <Tabs.Tab value="gaps">Gaps ({gaps.length})</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="md">Compliance Overview</Title>
              <Progress.Root size="xl" mb="md">
                <Progress.Section value={guideline.compliance_score} color="green">
                  <Progress.Label>{guideline.compliance_score.toFixed(1)}%</Progress.Label>
                </Progress.Section>
              </Progress.Root>

              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Last Assessment</Text>
                  <Text size="md" fw={500}>
                    {guideline.last_assessment_date
                      ? format(new Date(guideline.last_assessment_date), 'PPP')
                      : 'Not assessed'}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Control Status</Text>
                  <Group gap="xs" mt={4}>
                    <Badge color="green" size="sm">
                      {guideline.compliant_controls} Pass
                    </Badge>
                    <Badge color="yellow" size="sm">
                      {guideline.partial_controls} Partial
                    </Badge>
                    <Badge color="red" size="sm">
                      {guideline.failed_controls} Fail
                    </Badge>
                  </Group>
                </Grid.Col>
              </Grid>
            </Card>

            {semantic_contract && (
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Title order={3} mb="md">Semantic Contract Summary</Title>
                <Stack gap="sm">
                  <div>
                    <Text size="sm" c="dimmed">Entities</Text>
                    <Text size="md">{semantic_contract.entity_count || 0} defined</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Business Rules</Text>
                    <Text size="md">{semantic_contract.rule_count || 0} rules</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Contract Version</Text>
                    <Text size="md">{semantic_contract.version || 'N/A'}</Text>
                  </div>
                </Stack>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Controls Tab */}
        <Tabs.Panel value="controls" pt="md">
          <Grid>
            {controls.map((control) => (
              <Grid.Col key={control.id} span={{ base: 12, md: 6, lg: 4 }}>
                <ControlCard control={control} />
              </Grid.Col>
            ))}
            {controls.length === 0 && (
              <Grid.Col span={12}>
                <Paper p="xl" withBorder>
                  <Text c="dimmed" ta="center">No controls defined for this guideline</Text>
                </Paper>
              </Grid.Col>
            )}
          </Grid>
        </Tabs.Panel>

        {/* Gaps Tab */}
        <Tabs.Panel value="gaps" pt="md">
          <Stack gap="md">
            {gaps.map((gap) => (
              <GapCard key={gap.id} gap={gap} />
            ))}
            {gaps.length === 0 && (
              <Paper p="xl" withBorder>
                <Text c="dimmed" ta="center">No gaps identified for this guideline</Text>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* History Tab */}
        <Tabs.Panel value="history" pt="md">
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Compliance Trend</Title>
            {compliance_history && compliance_history.length > 0 ? (
              <ComplianceTrendChart data={compliance_history} />
            ) : (
              <Paper p="xl">
                <Text c="dimmed" ta="center">No historical data available</Text>
              </Paper>
            )}
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

// Sub-components

function ComplianceScoreBadge({ score }: { score: number }) {
  const getColor = (score: number) => {
    if (score >= 85) return 'green';
    if (score >= 70) return 'yellow';
    return 'red';
  };

  return (
    <Badge size="xl" color={getColor(score)} variant="filled">
      {score.toFixed(1)}% Compliant
    </Badge>
  );
}

function TrendBadge({ trend }: { trend: string }) {
  const config = {
    improving: { color: 'green', icon: IconTrendingUp, label: 'Improving' },
    declining: { color: 'red', icon: IconTrendingDown, label: 'Declining' },
    stable: { color: 'blue', icon: null, label: 'Stable' },
  }[trend] || { color: 'gray', icon: null, label: trend };

  const Icon = config.icon;

  return (
    <Badge
      size="lg"
      color={config.color}
      variant="light"
      leftSection={Icon && <Icon size={14} />}
    >
      {config.label}
    </Badge>
  );
}

function StatCard({
  title,
  value,
  color,
  critical,
}: {
  title: string;
  value: number;
  color: string;
  critical?: number;
}) {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Text size="sm" c="dimmed" mb={4}>
        {title}
      </Text>
      <Group justify="apart" align="flex-end">
        <Text size="xl" fw={700} c={color}>
          {value}
        </Text>
        {critical !== undefined && critical > 0 && (
          <Badge color="red" size="sm">
            {critical} Critical
          </Badge>
        )}
      </Group>
    </Card>
  );
}

function ControlCard({ control }: { control: ComplianceControl }) {
  const getStatusColor = (status?: AssessmentStatus) => {
    switch (status) {
      case 'PASS':
        return 'green';
      case 'FAIL':
        return 'red';
      case 'PARTIAL':
        return 'yellow';
      case 'NOT_TESTED':
      default:
        return 'gray';
    }
  };

  const getFrequencyBadge = (frequency: string) => {
    const colors: Record<string, string> = {
      CONTINUOUS: 'blue',
      DAILY: 'cyan',
      WEEKLY: 'teal',
      MONTHLY: 'green',
      QUARTERLY: 'yellow',
      ANNUAL: 'orange',
    };
    return colors[frequency] || 'gray';
  };

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder h="100%">
      <Stack gap="sm">
        <Group justify="apart" align="flex-start">
          <Badge size="sm" variant="outline">
            {control.control_id}
          </Badge>
          {control.last_assessment && (
            <Badge size="sm" color={getStatusColor(control.last_assessment)}>
              {control.last_assessment}
            </Badge>
          )}
        </Group>

        <Text size="sm" fw={500} lineClamp={3}>
          {control.description}
        </Text>

        <Divider />

        <Group gap="xs">
          <Badge size="xs" color={getFrequencyBadge(control.frequency)} variant="light">
            {control.frequency}
          </Badge>
          <Badge size="xs" color="gray" variant="light">
            {control.control_type}
          </Badge>
        </Group>

        {control.owner && (
          <Text size="xs" c="dimmed">
            Owner: {control.owner}
          </Text>
        )}

        {control.last_assessment_date && (
          <Text size="xs" c="dimmed">
            Last assessed: {format(new Date(control.last_assessment_date), 'PP')}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function GapCard({ gap }: { gap: ComplianceGap }) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'red';
      case 'HIGH':
        return 'orange';
      case 'MEDIUM':
        return 'yellow';
      case 'LOW':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RESOLVED':
        return 'green';
      case 'IN_PROGRESS':
        return 'blue';
      case 'CLOSED':
        return 'gray';
      case 'OPEN':
      default:
        return 'red';
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="apart" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group gap="xs" mb="xs">
              <Badge color={getSeverityColor(gap.severity)} size="sm">
                {gap.severity}
              </Badge>
              <Badge color={getStatusColor(gap.status)} size="sm" variant="light">
                {gap.status}
              </Badge>
              <Badge color="gray" size="sm" variant="outline">
                {gap.gap_type.replace(/_/g, ' ')}
              </Badge>
            </Group>
            <Title order={4}>{gap.title}</Title>
          </div>
        </Group>

        <Text size="sm">{gap.description}</Text>

        {gap.affected_entities.length > 0 && (
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              Affected Entities
            </Text>
            <Group gap="xs">
              {gap.affected_entities.map((entity, idx) => (
                <Badge key={idx} size="xs" variant="dot">
                  {entity}
                </Badge>
              ))}
            </Group>
          </div>
        )}

        {gap.remediation_steps.length > 0 && (
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              Remediation Steps
            </Text>
            <Stack gap={4}>
              {gap.remediation_steps.map((step, idx) => (
                <Text key={idx} size="xs">
                  {idx + 1}. {step}
                </Text>
              ))}
            </Stack>
          </div>
        )}

        <Group justify="apart">
          <div>
            {gap.assigned_to && (
              <Text size="xs" c="dimmed">
                Assigned: {gap.assigned_to}
              </Text>
            )}
          </div>
          <div>
            {gap.due_date && (
              <Text size="xs" c="dimmed">
                Due: {format(new Date(gap.due_date), 'PP')}
              </Text>
            )}
          </div>
        </Group>
      </Stack>
    </Card>
  );
}

function ComplianceTrendChart({ data }: { data: ComplianceTrendDataPoint[] }) {
  // Transform data for Mantine charts
  const chartData = data.map((point) => ({
    date: format(new Date(point.date), 'MMM dd'),
    score: point.compliance_score,
    gaps: point.gaps_count,
  }));

  return (
    <div style={{ height: 400 }}>
      <LineChart
        h={350}
        data={chartData}
        dataKey="date"
        series={[
          { name: 'score', label: 'Compliance Score (%)', color: 'blue' },
          { name: 'gaps', label: 'Gaps Count', color: 'red' },
        ]}
        curveType="natural"
        gridAxis="xy"
        withLegend
        legendProps={{ verticalAlign: 'bottom', height: 50 }}
        tickLine="xy"
        yAxisProps={{
          domain: [0, 100],
        }}
      />
    </div>
  );
}
